import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import Input from "src/components/inputs/Input";
import Toggle from "src/components/toggles/Toggle";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { number, object, string } from "yup";
import {
  useCreateMembership,
  useGetMembershipData,
  useUpdateMembership,
} from "../api-clients";

const FORM_FIELDS = {
  NAME: "name",
  DURATION: "duration",
  PRICE: "price",
  CREDIT: "credit",
  COMMISSION: "commission",
  STATUS: "status",
};

const CreateMembership = ({
  isInViewMode = false,
}: {
  isInViewMode?: boolean;
}) => {
  useSetActiveTab(TAB_NAMES.MEMBERSHIP_MANAGEMENT);
  const navigate = useNavigate();
  const { membershipId: id } = useParams();

  const membershipId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!membershipId, [membershipId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetCourseLoading, data } =
    useGetMembershipData(membershipId);

  const courseData = useMemo(() => data?.data ?? {}, [data?.data]);

  const {
    isLoading: isUpdateLoading,
    execute: updateCourse,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateMembership(membershipId);

  const { isLoading, isSuccess, execute, error } = useCreateMembership();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.NAME]: courseData?.[FORM_FIELDS.NAME] ?? "",
      [FORM_FIELDS.PRICE]: courseData?.[FORM_FIELDS.PRICE] ?? null,
      [FORM_FIELDS.DURATION]: courseData?.[FORM_FIELDS.DURATION] ?? null,
      [FORM_FIELDS.CREDIT]: courseData?.[FORM_FIELDS.CREDIT] ?? null,
      [FORM_FIELDS.COMMISSION]: courseData?.[FORM_FIELDS.COMMISSION] ?? null,
      [FORM_FIELDS.STATUS]: courseData?.[FORM_FIELDS.STATUS] ?? false,
    },
    validationSchema: object({
      [FORM_FIELDS.NAME]: string().required("This is a Required field."),
      [FORM_FIELDS.PRICE]: number().required("This is a Required field."),
      [FORM_FIELDS.DURATION]: number().required("This is a Required field."),
      [FORM_FIELDS.CREDIT]: number().required("This is a Required field."),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        updateCourse(data);
        return;
      }
      execute(data);
    },
  });

  const { values, errors, handleSubmit, setFieldValue, touched } = formik;

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(
        isUpdateSuccess ? "Membership Updated" : "Membership Created."
      );

      navigate("/store/memberships");
    }
  }, [isSuccess, isUpdateSuccess, navigate, successNotification]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () => isLoading || isGetCourseLoading || isUpdateLoading,
    [isGetCourseLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label="Add Membership" showBreadcrumb />

      <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Membership Name"
            placeholder="Membership Name"
            name={FORM_FIELDS.NAME}
            value={values[FORM_FIELDS.NAME] as string}
            error={getFieldError(FORM_FIELDS.NAME)}
            onChange={(e) => setFieldValue(FORM_FIELDS.NAME, e.target.value)}
            required
            disabled={isFieldDisabled}
          />
          <Input
            type="number"
            label="Duration"
            placeholder="Duration"
            name={FORM_FIELDS.DURATION}
            value={values[FORM_FIELDS.DURATION] as string}
            error={getFieldError(FORM_FIELDS.DURATION)}
            onChange={(e) =>
              setFieldValue(FORM_FIELDS.DURATION, e.target.value)
            }
            required
            disabled={isFieldDisabled}
          />
          <Input
            type="number"
            label="Price (₹)"
            placeholder="Price"
            name={FORM_FIELDS.PRICE}
            value={values[FORM_FIELDS.PRICE] as string}
            error={getFieldError(FORM_FIELDS.PRICE)}
            onChange={(e) => setFieldValue(FORM_FIELDS.PRICE, e.target.value)}
            required
            disabled={isFieldDisabled}
          />

          <Input
            type="number"
            label="Membership Credit"
            placeholder="Membership Credit"
            name={FORM_FIELDS.CREDIT}
            value={values[FORM_FIELDS.CREDIT] as string}
            error={getFieldError(FORM_FIELDS.CREDIT)}
            onChange={(e) => setFieldValue(FORM_FIELDS.CREDIT, e.target.value)}
            required
            disabled={isFieldDisabled}
          />

          <Input
            type="number"
            label="Commission"
            placeholder="Commission"
            name={FORM_FIELDS.COMMISSION}
            value={values[FORM_FIELDS.COMMISSION] as string}
            error={getFieldError(FORM_FIELDS.COMMISSION)}
            onChange={(e) =>
              setFieldValue(FORM_FIELDS.COMMISSION, e.target.value)
            }
            disabled={isFieldDisabled}
            rightIcon={() => (
              <div className="h-full border-l-[1px] flex justify-center items-center p-2 absolute right-0 text-gray-suva text-14px">
                %
              </div>
            )}
          />

          <Toggle
            label="Status"
            checked={values[FORM_FIELDS.STATUS] as boolean}
            onToggle={(val) => [setFieldValue(FORM_FIELDS.STATUS, val)]}
            disabled={isFieldDisabled}
            showToggleStatus
          />
        </div>

        <Button
          text={isLoading ? "Loading..." : "Submit"}
          onClick={handleSubmit}
          primary
          className="h-[40px] px-10"
          isDisabled={isFieldDisabled}
        />
      </div>
    </div>
  );
};

export default CreateMembership;
